<script lang="ts">
/**
 * "While you were asleep": one part, one RFQ, one loop of about 22 s. Everything on screen is about
 * enclosure 10001125 rev B, and every number the agents cite is shown somewhere in the story.
 * 1. Autonomy (left, the inbox): at 02:14, with the team offline, the RFQ lands with its drawing
 *    (PDF) and 3D model (STEP), and the Pre6 Agent picks it up on its own.
 * 2. The drawing moment (an overlay on the whole stage): the Quality Agent opens both files. They
 *    lift out of the email and open large, a scan line reads the drawing while the model turns, and
 *    reticles lock onto the Ø36 bore on the drawing and the model at once. The linked callout then
 *    flies into the thread as the critical dimension.
 * 3. Collaboration (center, the agents' thread): the agents @mention each other and pass findings
 *    along. The crew strip in its header shows who is working and on what: the active avatar opens
 *    into a pill with typing dots and a short label.
 * 4. Organizational memory (right): before each agent answers it searches the shop's past work on
 *    this part. The matches light up and the one it cites flies into the thread as an attachment.
 * At 08:30 the inbox shows the proposal, built from those findings, and a person approves it.
 *
 * Timing lives in the markup: any element with `data-at` (and optionally `data-until`) is switched
 * on by one clock, and CSS transitions do the motion. JS only types the search queries, flies the
 * files, the callout and the attachments along arcs, sweeps the scan line, turns the model, draws
 * the link between the two reticles, moves the person's cursor and pings @mentioned agents.
 *
 * Motion follows Lasseter's principles: the file chips dip before they lift and typing dots come
 * before every message (anticipation), one focal moment at a time with everything else dimmed or
 * covered (staging), files, callout and attachments travel on arcs and settle with a small
 * overshoot (arcs, follow-through), eased everything (slow in and out), the click squashes the
 * cursor (squash and stretch), and quiet secondary motion (the model's turn, the unread dot, the
 * clock, the mention pings).
 */
import Box from "lucide-svelte/icons/box";
import Calculator from "lucide-svelte/icons/calculator";
import Check from "lucide-svelte/icons/check";
import CornerDownRight from "lucide-svelte/icons/corner-down-right";
import Cog from "lucide-svelte/icons/cog";
import Crosshair from "lucide-svelte/icons/crosshair";
import FileText from "lucide-svelte/icons/file-text";
import Library from "lucide-svelte/icons/library";
import Mail from "lucide-svelte/icons/mail";
import Moon from "lucide-svelte/icons/moon";
import Network from "lucide-svelte/icons/network";
import Search from "lucide-svelte/icons/search";
import Sun from "lucide-svelte/icons/sun";
import { onMount } from "svelte";

type Agent = "pre6" | "rfq" | "prod" | "qa";
const AGENT: Record<Agent, { name: string; color: string; icon: typeof Cog }> = {
	pre6: { name: "Pre6 Agent", color: "var(--agent-pre6)", icon: Network },
	rfq: { name: "RFQ Agent", color: "var(--agent-rfq)", icon: Calculator },
	prod: { name: "Production Agent", color: "var(--agent-prod)", icon: Cog },
	qa: { name: "Quality Agent", color: "var(--agent-qa)", icon: Crosshair },
};
const ORDER: Agent[] = ["pre6", "rfq", "prod", "qa"];

/** The one part this story is about. */
const PN = "10001125";
const BORE = "Ø36";
const TOL = "+0.25 / −0.10";
const FILES = [
	{ name: `${PN}_RevB.PDF`, tag: "Drawing · rev B", src: "/images/platform/enclosure-drawing.webp", w: 740, h: 520 },
	{ name: `${PN}_RevB.STEP`, tag: "3D model", src: "/images/platform/enclosure-model.webp", w: 960, h: 938 },
];

const LOOP = 18.4;
/**
 * The story clock runs at this fraction of real time, so the loop takes about 22 s and every beat
 * has room to breathe. All the times in this file are story time.
 */
const SPEED = 0.78;
const END = 17.6;
/** The team wakes up: the clock flips and the review appears. */
const WAKE = 14.1;
const STATIC_T = WAKE + 2;

/** The drawing moment. */
const OPEN = 2.25; // the files lift out of the email and open large
const SCAN = 2.85; // a scan line reads the drawing
const SCAN_DUR = 0.95;
const LOCK = 3.95; // reticles lock onto the bore on both files
const CLOSE = 5.25; // the files fold back into the email, the callout flies into the thread
const LAND = 5.8; // the callout lands in the Quality Agent's message

/**
 * Where the bore sits on the two images, in % of each image. On the drawing it is measured off the
 * image: centered at (279, 202) of 740 × 520, outer circle about 40 px across the radius, so the
 * ring sits 5 px outside it. On the model it is placed by eye.
 */
const BORE_ON = {
	drawing: { x: (279 / 740) * 100, y: (202 / 520) * 100, w: (90 / 740) * 100, ar: 1 },
	model: { x: 34.9, y: 41.2, w: 20.5, ar: 1.32 },
};
/**
 * The one dimension the review highlights: the bore's tolerance note, Ø36.00 +0.25 / −0.10, as a
 * pixel box on the 740 × 520 drawing (measured off the image), padded by 2 px and placed in % of
 * the image. It lights when the reticles lock.
 */
const box = (x1: number, y1: number, x2: number, y2: number) => ({
	x: ((x1 - 2) / 740) * 100,
	y: ((y1 - 2) / 520) * 100,
	w: ((x2 - x1 + 5) / 740) * 100,
	h: ((y2 - y1 + 5) / 520) * 100,
});
const NOTE = box(655, 493, 724, 516);

/** How many past jobs the memory panel says it holds. */
const MEMORY_SIZE = 668;
/** Past jobs in memory, newest last. */
const MEMORY = [
	{ year: 2017, id: "Q-0233", what: "Pump cover · sand cast" },
	{ year: 2019, id: "Q-0412", what: `${PN} rev A · ₹1,150 / pc` },
	{ year: 2019, id: "ECN-0431", what: `${BORE} bore undersize · ream added` },
	{ year: 2021, id: "ECN-0517", what: `${BORE} tolerance set to ${TOL}` },
	{ year: 2021, id: "DWG-118", what: `Fixture F-7 · ${PN}` },
	{ year: 2022, id: "Q-0588", what: `${PN} rev A repeat · ₹1,190 / pc` },
	{ year: 2023, id: "Q-0602", what: "Cover · HPDC · ₹640 / pc" },
];

/**
 * The three memory searches: who asks, what they type, which jobs match, which one they cite, and
 * what the crew strip says while they search and then while they write it up.
 * Each turn: query typed at `at`, typing dots at +0.85, message at +1.4, attachment lands at +1.9.
 */
const SEARCHES: { agent: Agent; at: number; text: string; hits: number[]; take: number; doing: [string, string]; writing: [string, string] }[] = [
	{
		agent: "qa",
		at: 6.05,
		text: `${BORE} bore · past ECNs`,
		hits: [2, 3],
		take: 2,
		doing: [`Checking past ECNs on ${BORE}…`, "Past ECNs…"],
		writing: ["Briefing Production…", "Briefing…"],
	},
	{
		agent: "prod",
		at: 8.15,
		text: `fixture · ${PN}`,
		hits: [4],
		take: 4,
		doing: ["Finding a fixture for rev B…", "Fixtures…"],
		writing: ["Adding Op 30…", "Adding Op 30…"],
	},
	{
		agent: "rfq",
		at: 9.3, // while Production is still writing up Op 30: two agents at work at once
		text: `quotes · ${PN}`,
		hits: [1, 5],
		take: 5,
		doing: [`Pulling ${PN} quotes…`, "Past quotes…"],
		writing: ["Asking Production…", "Asking…"],
	},
];
const TYPE_DUR = 0.45;
const land = (s: (typeof SEARCHES)[number]) => s.at + 1.9;

/**
 * The thread. `typing` is when the dots show, `at` when the message lands, `pings` who it @mentions,
 * `reply` the earlier message it answers (by index, with the line it quotes). The middle of it is a
 * push-back: the RFQ Agent asks to drop the ream to save ₹50, the Quality Agent says why it stays.
 */
type Part = string | { at: Agent } | { b: string } | { pop: string };
type Message = {
	agent: Agent;
	typing: number;
	at: number;
	pings: Agent[];
	parts: Part[];
	attach?: number | "bore";
	reply?: { to: number; quote: string };
};
/** Typing takes longer for a longer message: 0.4 s for a short one, up to 0.75 s (timing). */
const typeTime = (parts: Part[]) => {
	const chars = parts.reduce((n, p) => n + (typeof p === "string" ? p.length : "at" in p ? 10 : "b" in p ? p.b.length : p.pop.length), 0);
	return Math.min(0.75, 0.4 + chars * 0.003);
};
/** A message that answers a search: it lands 1.4 after the search, typed for as long as it is long. */
const after = (s: (typeof SEARCHES)[number], parts: Part[]) => ({ typing: Math.max(s.at + 0.75, s.at + 1.4 - typeTime(parts)), at: s.at + 1.4, parts });
/** A message landing at `at`, typed for as long as it is long. */
const said = (at: number, parts: Part[]) => ({ typing: at - typeTime(parts), at, parts });
/** The push-back's answer, the price and the proposal land at these story times. */
const ANSWER = 12.15;
const PRICED = 13.0;
const ASSEMBLED = 13.75;
const THREAD: Message[] = [
	{
		agent: "pre6",
		typing: 1.3,
		at: 1.75,
		pings: ["rfq", "prod", "qa"],
		parts: [`New RFQ: enclosure ${PN} rev B, 5,000 pcs. `, { at: "rfq" }, " quote it, ", { at: "prod" }, " plan the ops, ", { at: "qa" }, " check the drawing."],
	},
	{
		agent: "qa",
		typing: 5.05,
		at: 5.4,
		pings: [],
		parts: [`${BORE} bore is the `, { b: "critical dimension" }, ". Checking its history."],
		attach: "bore",
	},
	{
		agent: "qa",
		...after(SEARCHES[0], [`2 past ECNs on ${BORE}. It came off the die `, { b: "undersize in 2019" }, ". ", { at: "prod" }, " add a finish ream."]),
		pings: ["prod"],
		attach: 0,
	},
	{
		agent: "prod",
		...after(SEARCHES[1], [`Added Op 30, finish ream on ${BORE}. Fixture F-7 from 2021 fits rev B, so there's `, { b: "no new tooling" }, " and lead time is ", { b: "4 weeks" }, ". ", { at: "rfq" }, " over to you."]),
		pings: ["rfq"],
		attach: 1,
		reply: { to: 2, quote: "Add a finish ream." },
	},
	{
		agent: "rfq",
		...after(SEARCHES[2], [{ at: "prod" }, " can we skip the ream? It adds ", { b: "₹50 / pc" }, "."]),
		pings: ["prod"],
		attach: 2,
	},
	// A beat of silence after the question and its quote land (land(SEARCHES[2]) = 11.2), then the answer.
	{
		agent: "qa",
		...said(ANSWER, [{ pop: "No." }, " Without the ream, it came off the die undersize in 2019. ", { at: "rfq" }, " keep Op 30."]),
		pings: ["rfq"],
		reply: { to: 4, quote: "Can we skip the ream?" },
	},
	{ agent: "rfq", ...said(PRICED, ["Keeping it. ", { b: "₹1,240 / pc" }, ", the 2022 repeat at ₹1,190 plus ₹50 for Op 30."]), pings: [] },
	{ agent: "pre6", ...said(ASSEMBLED, ["Proposal assembled and sent for review."]), pings: [] },
];

/** What each agent is doing, shown in the crew strip. [full label, short label for phones]. */
const CREW: { agent: Agent; from: number; to: number; label: [string, string] }[] = [
	{ agent: "pre6", from: 1.0, to: 1.8, label: ["Splitting the work…", "Splitting…"] },
	// While the files are open, the Quality Agent speaks from the viewer (see `.v-status`), not from here.
	{ agent: "qa", from: 1.95, to: OPEN, label: ["Reading drawing rev B…", "Reading rev B…"] },
	...SEARCHES.flatMap((s) => [
		{ agent: s.agent, from: s.at - 0.05, to: s.at + 0.85, label: s.doing },
		{ agent: s.agent, from: s.at + 0.85, to: land(s), label: s.writing },
	]),
	{ agent: "qa", from: ANSWER - 0.7, to: ANSWER, label: ["Pulling ECN-0431…", "ECN-0431…"] },
	{ agent: "rfq", from: PRICED - 0.65, to: PRICED, label: ["Costing the housing…", "Costing…"] },
	{ agent: "pre6", from: ASSEMBLED - 0.6, to: ASSEMBLED + 0.15, label: ["Assembling the proposal…", "Assembling…"] },
];

const ARIA = `Animation: at 2:14 in the morning, with the team offline, an RFQ for enclosure ${PN} rev B, 5,000 pieces, arrives with its drawing and 3D model, and the Pre6 Agent picks it up on its own. The Quality Agent opens both files, locks onto the Ø36 bore on the drawing and the model, tolerance plus 0.25 minus 0.10, and flags it as the critical dimension. In a shared thread the agents search the shop's memory of this part and hand the work to each other: two past ECNs on the bore, fixture F-7 from 2021 and the 2022 repeat order at ₹1,190 per piece. Production adds a finish ream as Op 30 with a 4 week lead time. The RFQ Agent asks to drop the ream to save ₹50, the Quality Agent says no because the bore came off the die undersize without it, and the RFQ Agent keeps it and prices the part at ₹1,240 per piece. At 8:30 the proposal is waiting in the inbox and a person approves it.`;

const clamp01 = (v: number) => Math.min(1, Math.max(0, v));
const inOut = (u: number) => (u < 0.5 ? 4 * u * u * u : 1 - (-2 * u + 2) ** 3 / 2);
const outBack = (u: number) => 1 + 2.4 * (u - 1) ** 3 + 1.4 * (u - 1) ** 2;
const qb = (a: number, c: number, b: number, e: number) => (1 - e) * (1 - e) * a + 2 * (1 - e) * e * c + e * e * b;

/**
 * Three captions under the stage name what sets these agents apart from a chat assistant. They come
 * in one by one as the story reaches them (the inbox starts it, the organization's memory grounds it,
 * a person approves the finished draft); the one playing now is lit, the earlier ones dim back.
 */
const CAPTIONS = ["Started by your inbox, not a prompt", "Grounded in decades of your organizational memory", "Hands you a finished draft to approve"];
const CAPTION_AT = [0, SEARCHES[0].at, WAKE];

let root: HTMLDivElement;
let captionsEl: HTMLOListElement;
let msgsEl: HTMLDivElement;
let live = $state(false);

onMount(() => {
	const reduced = matchMedia("(prefers-reduced-motion: reduce)").matches;
	const one = <T extends Element = HTMLElement>(s: string) => root.querySelector<T>(s);
	const all = <T extends Element = HTMLElement>(s: string) => Array.from(root.querySelectorAll<T>(s));
	const timed = all("[data-at]");
	const cards = all(".job");
	const avatars = all(".crew [data-agent]");
	const flyEls = all(".fly");
	const slots = all(".attach.mem");
	const query = one(".q-text");
	const queryWho = one(".q-who");
	const memoryEl = one(".memory");
	const human = one(".human");
	const approve = one(".approve");
	const files = one(".files");
	const thumbs = all(".file-thumb");
	const sheets = one(".sheets");
	const docs = all(".doc");
	const scan = one(".scan");
	const tilt = one(".tilt");
	const reticles = all(".reticle");
	const link = one<SVGPathElement>(".link path");
	const callout = one(".callout");
	const boreSlot = one(".attach.bore");
	const linkDots = all<SVGCircleElement>(".link circle");

	const rel = (el: Element) => {
		const a = el.getBoundingClientRect();
		const b = root.getBoundingClientRect();
		return { x: a.left - b.left, y: a.top - b.top, w: a.width, h: a.height };
	};
	const center = (el: Element) => {
		const r = rel(el);
		return { x: r.x + r.w / 2, y: r.y + r.h / 2 };
	};
	/** The link between the two reticles and its apex, where the callout sits. Kept from the last open frame. */
	let arc: { d: string; x: number; y: number } | null = null;

	/** Whoever acted last by story time `t`: started typing, posted, or started a search. */
	const ACTS = [...THREAD.flatMap((m) => [{ t: m.typing, a: m.agent }, { t: m.at, a: m.agent }]), ...SEARCHES.map((x) => ({ t: x.at, a: x.agent }))].sort(
		(p, q) => p.t - q.t,
	);
	const actorAt = (t: number) => {
		let a: Agent = "pre6";
		for (const x of ACTS) if (x.t <= t) a = x.a;
		return a;
	};

	let lastFocus = "";
	let lastColor = "";
	let lastAssist = "";
	const memoryShown = () => !!memoryEl && memoryEl.offsetParent !== null;

	/**
	 * Which panel holds the eye at story time `t`, the color of the agent acting there, and a panel
	 * that stays half lit beside it. The story is the agents working together, so the thread holds the
	 * eye through the searches: memory takes the spotlight once, while the first search types (to show
	 * where the answers come from), and after that it only stays half lit while an agent searches it.
	 */
	function stageAt(t: number): [focus: string, color: string, assist: string] {
		if (t < 1.15) return ["inbox", AGENT.pre6.color, ""];
		if (t < OPEN) return ["thread", AGENT.pre6.color, ""];
		if (t < CLOSE + 0.3) return ["", AGENT.qa.color, ""];
		if (t < SEARCHES[0].at) return ["thread", AGENT.qa.color, ""];
		if (t < ASSEMBLED - 0.6) {
			let s = SEARCHES[0];
			for (const x of SEARCHES) if (t >= x.at) s = x;
			const searching = t < s.at + 0.85 && memoryShown();
			if (searching && s === SEARCHES[0]) return ["memory", AGENT[s.agent].color, ""];
			return ["thread", AGENT[actorAt(t)].color, searching ? "memory" : ""];
		}
		if (t < WAKE) return ["thread", AGENT.pre6.color, ""];
		return ["inbox", AGENT.pre6.color, ""];
	}

	function render(t: number) {
		for (const el of timed) {
			const at = Number(el.dataset.at);
			const until = el.dataset.until ? Number(el.dataset.until) : END;
			el.toggleAttribute("data-on", t >= at && t < until);
		}
		root.toggleAttribute("data-fade", t >= END);
		if (captionsEl) {
			let step = 0;
			CAPTION_AT.forEach((at, i) => {
				if (t >= at) step = i;
			});
			if (captionsEl.dataset.step !== String(step)) captionsEl.dataset.step = String(step);
		}

		// Staging: one panel holds the eye at a time, ringed in the acting agent's color, while the others
		// step back. The drawing review brings its own spotlight, so nothing is staged under it.
		if (live) {
			const [focus, color, assist] = stageAt(t);
			if (focus !== lastFocus) {
				if (focus) root.dataset.focus = focus;
				else delete root.dataset.focus;
				lastFocus = focus;
			}
			if (assist !== lastAssist) {
				if (assist) root.dataset.assist = assist;
				else delete root.dataset.assist;
				lastAssist = assist;
			}
			if (color !== lastColor) {
				root.style.setProperty("--fc", color);
				lastColor = color;
			}
		}

		// The crew strip: the working agent opens into a pill; @mentions ping the agents they name.
		for (const av of avatars) {
			const a = av.dataset.agent as Agent;
			av.toggleAttribute("data-open", CREW.some((c) => c.agent === a && t >= c.from && t < c.to));
			av.toggleAttribute("data-ping", THREAD.some((m) => m.pings.includes(a) && t >= m.at && t < m.at + 0.7));
		}

		// The drawing moment. The file chips dip (anticipation), then the files lift out of the email.
		files?.toggleAttribute("data-press", t >= OPEN - 0.22 && t < OPEN);
		files?.toggleAttribute("data-out", live && t >= OPEN && t < CLOSE + 0.42);
		const open = live && !!sheets && t >= OPEN && t < CLOSE + 0.45;
		docs.forEach((doc, k) => {
			const thumb = thumbs[k];
			const frame = doc.querySelector<HTMLElement>(".frame");
			if (!open || !sheets || !thumb || !frame) {
				doc.style.opacity = "0";
				return;
			}
			// The frame's resting box (offsets ignore the transform), and the thumbnail it grows from.
			const fx = root.clientLeft + sheets.offsetLeft + doc.offsetLeft + frame.offsetLeft;
			const fy = root.clientTop + sheets.offsetTop + doc.offsetTop + frame.offsetTop;
			const a = rel(thumb);
			const closing = t >= CLOSE;
			// The model leaves a beat after the drawing (overlapping action).
			const u = closing ? 1 - clamp01((t - CLOSE - k * 0.05) / 0.4) : clamp01((t - OPEN - k * 0.14) / 0.55);
			const e = inOut(u);
			const s0 = a.w / frame.offsetWidth;
			const s = s0 + (1 - s0) * (closing ? e : outBack(u));
			const cx = (a.x + fx) / 2;
			const cy = Math.min(a.y, fy) - 85;
			const x = qb(a.x, cx, fx, e);
			// It flies on a high arc, then bobs as it lands: a 3 px dip and back (follow-through).
			const st = clamp01((t - (OPEN + k * 0.14 + 0.55)) / 0.35);
			const bob = !closing && st > 0 && st < 1 ? Math.sin(st * Math.PI * 2) * 3 * (1 - st) : 0;
			const y = qb(a.y, cy, fy, e) + bob;
			doc.style.transformOrigin = `${frame.offsetLeft}px ${frame.offsetTop}px`;
			doc.style.transform = `translate3d(${(x - fx).toFixed(1)}px, ${(y - fy).toFixed(1)}px, 0) rotate(${(Math.sin(u * Math.PI) * (k ? 4 : -4)).toFixed(2)}deg) scale(${s.toFixed(4)})`;
			doc.style.opacity = clamp01(u / (closing ? 0.5 : 0.12)).toFixed(3);
		});

		// The scan line reads the drawing.
		const p = inOut(clamp01((t - SCAN) / SCAN_DUR));
		scan?.style.setProperty("--p", p.toFixed(4));
		scan?.toggleAttribute("data-on", t >= SCAN && t < SCAN + SCAN_DUR + 0.1);

		// The model turns slowly while open, and comes to rest as the reticles lock.
		if (tilt) {
			const k = inOut(clamp01((t - OPEN) / (LOCK - OPEN)));
			const drift = clamp01((t - LOCK) / (CLOSE - LOCK));
			tilt.style.transform = `rotateX(${(8 - 5 * k).toFixed(2)}deg) rotateY(${(-11 + 14 * k + 3 * drift).toFixed(2)}deg)`;
		}

		// A straight leader joins the two reticles, a dot at each end, like the sector sheets' leaders;
		// the callout pops at its middle.
		if (link && reticles.length === 2) {
			if (open && t < CLOSE) {
				const a = center(reticles[0]);
				const b = center(reticles[1]);
				const ox = root.clientLeft;
				const oy = root.clientTop;
				arc = {
					d: `M${(a.x - ox).toFixed(1)} ${(a.y - oy).toFixed(1)} L${(b.x - ox).toFixed(1)} ${(b.y - oy).toFixed(1)}`,
					x: (a.x + b.x) / 2,
					y: (a.y + b.y) / 2,
				};
				link.setAttribute("d", arc.d);
				[a, b].forEach((p, k) => {
					linkDots[k]?.setAttribute("cx", (p.x - ox).toFixed(1));
					linkDots[k]?.setAttribute("cy", (p.y - oy).toFixed(1));
				});
			}
			link.style.strokeDashoffset = (1 - inOut(clamp01((t - LOCK - 0.3) / 0.35))).toFixed(4);
			const shown = open && t >= LOCK + 0.3 ? (1 - clamp01((t - CLOSE) / 0.15)).toFixed(3) : "0";
			link.style.opacity = shown;
			for (const dot of linkDots) dot.style.opacity = shown;
		}
		if (callout) {
			const pop = clamp01((t - LOCK - 0.55) / 0.35);
			const f = clamp01((t - CLOSE) / (LAND - CLOSE));
			if (!live || !arc || !boreSlot || t < LOCK + 0.55 || t >= LAND) {
				callout.style.opacity = "0";
			} else {
				const w = callout.offsetWidth;
				const h = callout.offsetHeight;
				// Centered on the link, but never past the stage's edges on a narrow screen.
				const ax = Math.min(Math.max(8, arc.x - w / 2), root.clientWidth - w - 8);
				const ay = arc.y - h / 2;
				const b = rel(boreSlot);
				const e = inOut(f);
				const x = qb(ax, (ax + b.x) / 2, b.x, e);
				const y = qb(ay, Math.min(ay, b.y) - 60, b.y, e);
				const s = f > 0 ? 1 + (b.w / w - 1) * e + Math.sin(f * Math.PI) * 0.06 : 0.6 + 0.4 * outBack(pop);
				callout.style.transform = `translate3d(${x.toFixed(1)}px, ${y.toFixed(1)}px, 0) rotate(${(Math.sin(f * Math.PI) * -3).toFixed(2)}deg) scale(${s.toFixed(3)})`;
				callout.style.opacity = (clamp01(pop / 0.3) * (f > 0.94 ? (1 - f) / 0.06 : 1)).toFixed(3);
			}
		}

		// Memory: the current search types itself; its matches light up and the rest dim.
		let search: (typeof SEARCHES)[number] | null = null;
		for (const s of SEARCHES) if (t >= s.at && t < land(s) + 0.5) search = s;
		if (query && queryWho) {
			const text = search ? search.text.slice(0, Math.round(search.text.length * clamp01((t - search.at) / TYPE_DUR))) : "";
			if (query.textContent !== text) query.textContent = text;
			queryWho.style.setProperty("--c", search ? AGENT[search.agent].color : "transparent");
			queryWho.toggleAttribute("data-on", !!search);
		}
		const found = search && t >= search.at + TYPE_DUR;
		memoryEl?.toggleAttribute("data-searching", !!found);
		cards.forEach((el, i) => {
			const hit = !!found && !!search && search.hits.includes(i);
			el.toggleAttribute("data-hit", hit);
			el.toggleAttribute("data-taken", !!search && search.take === i && t >= land(search) - 0.45);
			el.toggleAttribute("data-cite", !!search && search.take === i && t >= land(search) - 0.8 && t < land(search) - 0.45);
			if (search) el.style.setProperty("--c", AGENT[search.agent].color);
		});

		// The cited memory flies into the thread on an arc and settles into its message.
		SEARCHES.forEach((s, k) => {
			const fly = flyEls[k];
			const slot = slots[k];
			const card = cards[s.take];
			// No flight when the memory panel is hidden; the attachment simply appears.
			if (!fly || !slot || !card || !card.offsetParent) {
				if (fly) fly.style.opacity = "0";
				return;
			}
			const u = (t - (land(s) - 0.45)) / 0.45;
			if (u < 0 || u > 1 || !live) {
				fly.style.opacity = "0";
				return;
			}
			const a = rel(card);
			const b = rel(slot);
			const e = inOut(u);
			const x = qb(a.x, (a.x + b.x) / 2, b.x, e);
			const y = qb(a.y, Math.min(a.y, b.y) - 40, b.y, e);
			fly.style.width = `${(a.w + (b.w - a.w) * e).toFixed(1)}px`;
			fly.style.transform = `translate3d(${x.toFixed(1)}px, ${y.toFixed(1)}px, 0) rotate(${(Math.sin(u * Math.PI) * -3).toFixed(2)}deg) scale(${(1 + Math.sin(u * Math.PI) * 0.06).toFixed(3)})`;
			fly.style.opacity = u > 0.94 ? ((1 - u) / 0.06).toFixed(3) : "1";
		});

		// A person arrives only at the end, to approve.
		if (human && approve) {
			const b = rel(approve);
			const tx = b.x + b.w * 0.55;
			const ty = b.y + b.h * 0.6;
			// The cursor decelerates into the button (slow in), rests a beat, then presses.
			const u = clamp01((t - (WAKE + 0.95)) / 0.75);
			const sx = tx + 120;
			const sy = ty + 90;
			const e = 1 - (1 - u) ** 3;
			const x = sx + (tx - sx) * e - Math.sin(e * Math.PI) * 20;
			const y = sy + (ty - sy) * e;
			const press = t - (WAKE + 1.8);
			const sq = press > 0 && press < 0.22 ? (press < 0.06 ? 1 - 0.22 * (press / 0.06) : 0.78 + 0.22 * outBack((press - 0.06) / 0.16)) : 1;
			human.style.transform = `translate3d(${x.toFixed(1)}px, ${y.toFixed(1)}px, 0) scale(${sq.toFixed(3)})`;
			human.style.opacity = (clamp01((t - (WAKE + 0.95)) / 0.2) * (1 - clamp01((t - (END - 0.3)) / 0.3))).toFixed(3);
		}
	}

	if (reduced) {
		render(STATIC_T);
		captionsEl?.toggleAttribute("data-still", true);
		return;
	}
	live = true;

	// The thread scrolls instead of reflowing: a spacer as tall as the window sits above the messages,
	// so new ones always land below the old ones, and the window scrolls to keep the newest in view.
	// Scrolling moves nothing in the layout, so the thread adds no layout shift as it grows.
	const inner = msgsEl?.querySelector<HTMLElement>(".msgs-in");
	const spacer = msgsEl?.querySelector<HTMLElement>(".spacer");
	const pin = () => {
		if (msgsEl) msgsEl.scrollTop = msgsEl.scrollHeight;
	};
	const ro = new ResizeObserver((entries) => {
		for (const e of entries) {
			if (e.target === msgsEl && spacer) spacer.style.height = `${msgsEl.clientHeight}px`;
		}
		pin();
	});
	if (msgsEl) ro.observe(msgsEl);
	if (inner) ro.observe(inner);

	let raf = 0;
	let last = 0;
	let clock = 0;
	let inView = false;
	const frame = (now: number) => {
		raf = requestAnimationFrame(frame);
		const dt = last ? Math.min((now - last) / 1000, 0.05) : 0;
		last = now;
		clock = (clock + dt * SPEED) % LOOP;
		render(clock);
	};
	const sync = () => {
		const run = inView && !document.hidden;
		if (run && !raf) {
			last = 0;
			raf = requestAnimationFrame(frame);
		} else if (!run && raf) {
			cancelAnimationFrame(raf);
			raf = 0;
		}
	};
	const io = new IntersectionObserver((e) => {
		inView = e.some((x) => x.isIntersecting);
		sync();
	});
	io.observe(root);
	document.addEventListener("visibilitychange", sync);
	// The page arrives showing the finished thread (for anyone without script). Going live, the rows
	// snap shut in one frame instead of animating closed, so the switch adds no layout shift.
	root.setAttribute("data-boot", "");
	render(0);
	requestAnimationFrame(() => requestAnimationFrame(() => root.removeAttribute("data-boot")));
	return () => {
		cancelAnimationFrame(raf);
		io.disconnect();
		ro.disconnect();
		document.removeEventListener("visibilitychange", sync);
	};
});
</script>

{#snippet avatar(a: Agent, size = 22)}
	{@const ag = AGENT[a]}
	<span class="av" style:--c={ag.color} style:--s="{size}px"><ag.icon size={Math.round(size * 0.55)} strokeWidth={2.25} /></span>
{/snippet}

{#snippet say(at: number, until: number, label: [string, string])}
	<span class="say" data-at={at} data-until={until}>
		<span class="say-in"><span class="say-dots"><i></i><i></i><i></i></span><span class="say-l">{label[0]}</span><span class="say-s">{label[1]}</span></span>
	</span>
{/snippet}

<div class="story" bind:this={root} role="img" aria-label={ARIA} data-live={live || undefined}>
	<!-- 1. The inbox: work arrives while the team is offline. -->
	<section class="panel inbox" aria-hidden="true">
		<header class="head">
			<span class="title"><Mail size={14} strokeWidth={2} />Inbox</span>
			<span class="clock">
				<span class="night" data-at="0" data-until={WAKE}><Moon size={12} strokeWidth={2.25} />02:14<span class="clk-x">&nbsp;· team offline</span></span>
				<span class="day" data-at={WAKE}><Sun size={12} strokeWidth={2.25} />08:30<span class="clk-x">&nbsp;· good morning</span></span>
			</span>
		</header>
		<ul class="mails">
			<li class="mail new" data-at="0.5">
				<span class="dot"></span>
				<span class="from">Sourcing · repeat customer</span>
				<span class="subj">RFQ: enclosure {PN} rev B, 5,000 pcs</span>
				<span class="files">
					{#each FILES as f (f.name)}
						<span class="file">
							<img class="file-thumb" src={f.src} width={f.w} height={f.h} alt="" fetchpriority="low" decoding="async" />{f.name}
						</span>
					{/each}
				</span>
				<span class="picked" data-at="0.95">{@render avatar("pre6", 16)}<span>Picked up by Pre6 Agent · <b>4 s</b> after it arrived</span></span>
			</li>
			<li class="mail"><span class="from">Purchasing</span><span class="subj">PO 4471 confirmed</span></li>
			<li class="mail"><span class="from">Dispatch</span><span class="subj">Lot 0918 shipped</span></li>
		</ul>
		<div class="review" data-at={WAKE + 0.3}>
			<span class="review-k">{@render avatar("pre6", 16)}Ready for your review</span>
			<span class="review-from">from #enclosure-rfq</span>
			<span class="review-t">Proposal · {PN} rev B</span>
			<span class="review-s"><b>₹1,240 / pc</b> · 5,000 pcs · 4 weeks</span>
			<span class="review-s"><b>{BORE} critical</b> · finish ream at Op 30</span>
			<span class="btns">
				<span class="btn ghost">Request changes</span>
				<span class="btn approve">
					<span class="ap-label" data-at="0" data-until={WAKE + 1.86}>Approve</span>
					<span class="ap-label done" data-at={WAKE + 1.86}><Check size={13} strokeWidth={2.75} />Approved</span>
				</span>
				<span class="by-you" data-at={WAKE + 2.15}>by you</span>
			</span>
		</div>
	</section>

	<!-- 2. The agents' thread: they split the work and talk it through. -->
	<section class="panel thread" aria-hidden="true">
		<header class="head">
			<span class="title"><span class="hash">#</span>enclosure-rfq</span>
			<span class="crew">
				{#each ORDER as a (a)}
					<span class="crew-av" data-agent={a}>
						{@render avatar(a, 24)}
						{#each CREW.filter((c) => c.agent === a) as c (c.from)}{@render say(c.from, c.to, c.label)}{/each}
					</span>
				{/each}
			</span>
		</header>
		<div class="msgs" bind:this={msgsEl}>
			<div class="msgs-in">
			<span class="spacer" aria-hidden="true"></span>
			<p class="sys" data-at="1.05">Pre6 Agent started this thread from the RFQ email</p>
			{#each THREAD as m, i (i)}
				<div class="row typing" data-at={m.typing} data-until={m.at}>
					{@render avatar(m.agent, 22)}<span class="dots"><i></i><i></i><i></i></span>
				</div>
				<div class="row msg" data-at={m.at}>
					<div class="msg-in">
						{@render avatar(m.agent, 22)}
						<div class="bubble">
							<span class="name" style:color={AGENT[m.agent].color}>{AGENT[m.agent].name}</span>
							{#if m.reply}
								{@const to = THREAD[m.reply.to].agent}
								<span class="reply" style:--c={AGENT[to].color}
									><CornerDownRight size={11} strokeWidth={2.25} /><b>{AGENT[to].name}</b><span>{m.reply.quote}</span></span
								>
							{/if}
							<p>
								{#each m.parts as part, j (j)}{#if typeof part === "string"}{part}{:else if "b" in part}<b>{part.b}</b>{:else if "pop" in part}<b class="pop">{part.pop}</b>{:else}<span
											class="mention"
											style:--c={AGENT[part.at].color}>@{AGENT[part.at].name.replace(" Agent", "")}</span
										>{/if}{/each}
							</p>
							{#if m.attach === "bore"}
								<span class="attach bore" data-at={LAND} style:--c={AGENT.qa.color}>
									<Crosshair size={12} strokeWidth={2.25} />
									<b>{BORE} {TOL}</b>
									<span>Critical · drawing rev B</span>
								</span>
							{:else if m.attach !== undefined}
								{@const s = SEARCHES[m.attach]}
								<span class="attach mem" data-at={land(s)} style:--c={AGENT[s.agent].color}>
									<Library size={12} strokeWidth={2.25} />
									<b>{MEMORY[s.take].id} · {MEMORY[s.take].year}</b>
									<span>{MEMORY[s.take].what}</span>
								</span>
							{/if}
						</div>
					</div>
				</div>
			{/each}
			</div>
		</div>
	</section>

	<!-- 3. The memory: past work the agents search before they answer. -->
	<section class="panel memory" aria-hidden="true">
		<header class="head">
			<span class="title"><Library size={14} strokeWidth={2} />Organizational memory</span>
			<span class="count">{MEMORY_SIZE} past jobs</span>
		</header>
		<div class="search">
			<Search size={13} strokeWidth={2.25} />
			<span class="q-who"></span>
			<span class="q-text"></span><span class="caret"></span>
		</div>
		<ol class="jobs">
			{#each MEMORY as job, i (job.id)}
				<li class="job" data-i={i}>
					<span class="yr">{job.year}</span>
					<span class="job-main"><b>{job.id}</b><span>{job.what}</span></span>
				</li>
			{/each}
		</ol>
	</section>

	<!-- The drawing moment: the RFQ's two files open over the stage and the bore is matched on both. -->
	<div class="viewer" data-at={OPEN} data-until={CLOSE + 0.3} aria-hidden="true"></div>
	<div class="sheets" aria-hidden="true">
		<span class="v-status" style:--c={AGENT.qa.color}>
			{@render avatar("qa", 20)}
			{@render say(OPEN, LOCK, ["Reading drawing rev B…", "Reading rev B…"])}
			{@render say(LOCK, CLOSE + 0.2, [`${BORE} bore matched on both files`, `${BORE} matched on both`])}
		</span>
		<div class="docs">
			{#each FILES as f, k (f.name)}
				{@const on = k ? BORE_ON.model : BORE_ON.drawing}
				<figure class="doc" class:model={k === 1}>
					<figcaption class="doc-head">
						{#if k}<Box size={13} strokeWidth={2} />{:else}<FileText size={13} strokeWidth={2} />{/if}
						<span class="doc-name">{f.name}</span>
						<span class="doc-tag">{f.tag}</span>
					</figcaption>
					<div class="frame" style:aspect-ratio="{f.w} / {f.h}">
						{#if k}
							<div class="tilt">
								<img src={f.src} width={f.w} height={f.h} alt="" fetchpriority="low" decoding="async" />
								<span class="reticle" data-at={LOCK} data-until={CLOSE + 0.1} style:--x="{on.x}%" style:--y="{on.y}%" style:--w="{on.w}%" style:--ar={on.ar}></span>
							</div>
						{:else}
							<img src={f.src} width={f.w} height={f.h} alt="" fetchpriority="low" decoding="async" />
							<span class="mark key" data-at={LOCK} data-until={CLOSE} style:left="{NOTE.x}%" style:top="{NOTE.y}%" style:width="{NOTE.w}%" style:height="{NOTE.h}%"></span>
							<span class="scan"></span>
							<span class="reticle" data-at={LOCK} data-until={CLOSE + 0.1} style:--x="{on.x}%" style:--y="{on.y}%" style:--w="{on.w}%" style:--ar={on.ar}></span>
						{/if}
					</div>
				</figure>
			{/each}
		</div>
	</div>
	<svg class="link" aria-hidden="true"><path pathLength="1" /><circle r="4" /><circle r="4" /></svg>
	<span class="attach callout" aria-hidden="true" style:--c={AGENT.qa.color}>
		<Crosshair size={12} strokeWidth={2.25} />
		<b>{BORE} {TOL}</b>
		<span>Critical · drawing rev B</span>
	</span>

	{#each SEARCHES as s, k (k)}
		<div class="fly" aria-hidden="true" style:--c={AGENT[s.agent].color}>
			<span class="yr">{MEMORY[s.take].year}</span>
			<span class="job-main"><b>{MEMORY[s.take].id}</b><span>{MEMORY[s.take].what}</span></span>
		</div>
	{/each}
	<svg class="human" viewBox="0 0 28 40" aria-hidden="true"><path d="M3 2 L3 31 L10.2 24.6 L15 36 L20.4 33.7 L15.6 22.6 L25 22.6 Z" /></svg>
</div>

<ol class="captions" bind:this={captionsEl}>
	{#each CAPTIONS as c, i (c)}<li data-i={i}><span class="c-n" aria-hidden="true">{i + 1}</span>{c}</li>{/each}
</ol>

<style>
	/* The captions under the stage, one at a time: the one for the part playing now rises into place
	   as the one before it fades away, all in one line's space. */
	.captions {
		--ease: cubic-bezier(0.2, 0.75, 0.2, 1);
		display: grid;
		justify-items: center;
		margin: 6px 0 0;
		padding: 0;
		list-style: none;
	}

	.captions li {
		grid-area: 1 / 1;
		display: inline-flex;
		align-items: center;
		gap: 9px;
		color: var(--ink);
		font-size: 14px;
		font-weight: 500;
		text-align: center;
		opacity: 0;
		transform: translateY(8px);
		transition:
			opacity 0.3s var(--ease),
			transform 0.35s var(--ease);
	}

	.c-n {
		display: grid;
		flex: none;
		place-items: center;
		width: 20px;
		height: 20px;
		border-radius: 6px;
		background: var(--primary);
		color: #fff;
		font: 600 11px/1 var(--font-mono);
	}

	.captions:global([data-step="0"]) li[data-i="0"],
	.captions:global([data-step="1"]) li[data-i="1"],
	.captions:global([data-step="2"]) li[data-i="2"] {
		opacity: 1;
		transform: none;
		transition:
			opacity 0.45s var(--ease) 0.2s,
			transform 0.55s var(--ease) 0.2s;
	}

	/* Reduced motion: no swapping; the three read as a short list. */
	.captions:global([data-still]) {
		display: flex;
		flex-wrap: wrap;
		justify-content: center;
		gap: 8px 28px;
	}

	.captions:global([data-still]) li {
		grid-area: auto;
		opacity: 1;
		transform: none;
	}

	/*
	 * Three panels on the page's own surface: inbox, thread, memory. Light, like the home page,
	 * with the agents' colors as the only accents.
	 */
	/* All text in the animation is one size, the page's small size; weight and color make the hierarchy. */
	.story {
		--ease: cubic-bezier(0.2, 0.75, 0.2, 1);
		--pop: cubic-bezier(0.34, 1.56, 0.64, 1);
		--line: var(--border);
		--qa: var(--agent-qa);
		position: relative;
		display: grid;
		grid-template-columns: minmax(0, 0.95fr) minmax(0, 1.35fr) minmax(0, 1fr);
		gap: 14px;
		aspect-ratio: 2 / 1;
		min-height: 470px;
		padding: 14px;
		overflow: hidden;
		border: 1px solid var(--line);
		border-radius: 12px;
		background:
			linear-gradient(oklch(0.6 0.03 255 / 0.05) 1px, transparent 1px) 0 0 / 24px 24px,
			linear-gradient(90deg, oklch(0.6 0.03 255 / 0.05) 1px, transparent 1px) 0 0 / 24px 24px,
			var(--surface);
		color: var(--foreground);
		font-family: "Inter Variable", var(--font-sans);
		transition: opacity 0.6s var(--ease);
	}

	.story:global([data-fade]) {
		opacity: 0.35;
	}

	.panel {
		display: flex;
		flex-direction: column;
		min-width: 0;
		min-height: 0;
		overflow: hidden;
		border: 1px solid var(--line);
		border-radius: 10px;
		background: #fff;
		box-shadow: 0 12px 30px -24px oklch(0.2 0.05 264 / 0.35);
		transition:
			opacity 0.5s var(--ease),
			transform 0.6s var(--ease),
			filter 0.5s var(--ease),
			box-shadow 0.5s var(--ease);
	}

	/* Staging: while one panel holds the eye, the others step back (dimmer, a touch smaller, less color),
	   and the one in focus is ringed in the acting agent's color. */
	.story:global([data-focus]) .panel {
		opacity: 0.45;
		filter: saturate(0.4);
		transform: scale(0.985);
	}

	/* A panel the focused one is drawing on (memory, while an agent searches it) stays half lit. */
	.story:global([data-assist="memory"]) .memory {
		opacity: 0.8;
		filter: saturate(0.8);
		transform: none;
	}

	.story:global([data-focus="inbox"]) .inbox,
	.story:global([data-focus="thread"]) .thread,
	.story:global([data-focus="memory"]) .memory {
		opacity: 1;
		filter: none;
		transform: none;
		box-shadow:
			0 0 0 1.5px color-mix(in oklch, var(--fc) 45%, transparent),
			0 22px 44px -26px color-mix(in oklch, var(--fc) 70%, transparent);
	}

	/* Hand-offs between panels show at both ends at once: the inbox's "Picked up" line and the thread's
	   opening line flash together, so the eye connects them without a line drawn across. */
	.picked:global([data-on]),
	.sys:global([data-on]) {
		border-radius: 6px;
		animation: sync 1.6s var(--ease);
	}

	@keyframes sync {
		0%,
		40% {
			background: color-mix(in oklch, var(--primary) 12%, transparent);
			box-shadow: 0 0 0 4px color-mix(in oklch, var(--primary) 12%, transparent);
		}
		100% {
			background: transparent;
			box-shadow: 0 0 0 4px transparent;
		}
	}

	/* The new RFQ email announces itself with a ring that swells and fades as it lands. */
	.mail.new:global([data-on])::after {
		content: "";
		position: absolute;
		inset: 4px;
		border-radius: 8px;
		box-shadow: 0 0 0 2px var(--primary);
		opacity: 0;
		pointer-events: none;
		animation: arrive 1.4s var(--ease) 0.2s 2;
	}

	@keyframes arrive {
		0% {
			opacity: 0.8;
			transform: scale(0.98);
		}
		100% {
			opacity: 0;
			transform: scale(1.01);
		}
	}

	.head {
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 8px;
		min-height: 42px;
		padding: 10px 12px;
		border-bottom: 1px solid var(--line);
	}

	/* The crew strip is 28px tall, so the thread's header trims its padding to stay level with the others. */
	.thread .head {
		padding-block: 6px;
	}

	.title {
		display: inline-flex;
		align-items: center;
		gap: 7px;
		color: var(--ink);
		font-size: 13px;
		font-weight: 600;
		white-space: nowrap;
	}

	.hash {
		color: var(--muted-foreground);
	}

	.count,
	.clock {
		color: var(--muted-foreground);
		font: 500 11px/1 var(--font-mono);
		white-space: nowrap;
	}

	.av {
		display: inline-grid;
		flex: none;
		place-items: center;
		width: var(--s);
		height: var(--s);
		border-radius: 6px;
		background: var(--c);
		color: #fff;
	}

	/* Anything switched on by the clock fades and rises in. */
	[data-at] {
		opacity: 0;
		transition:
			opacity 0.35s var(--ease),
			transform 0.45s var(--ease);
	}

	[data-at]:global([data-on]) {
		opacity: 1;
	}

	/* Inbox. */
	.clock {
		display: grid;
	}

	.clock > span {
		grid-area: 1 / 1;
		display: inline-flex;
		align-items: center;
		justify-content: flex-end;
		gap: 5px;
	}

	.night {
		color: var(--agent-rfq);
	}

	.day {
		color: var(--warn);
	}

	.mails {
		display: flex;
		flex-direction: column;
		margin: 0;
		padding: 0;
		list-style: none;
	}

	.mail {
		position: relative;
		display: flex;
		flex-direction: column;
		gap: 3px;
		padding: 11px 12px 11px 22px;
		border-bottom: 1px solid var(--line);
		color: var(--muted-foreground);
		font-size: 12px;
	}

	.mail:not(.new) {
		opacity: 0.6;
	}

	.from {
		font-weight: 600;
	}

	.subj {
		color: var(--foreground);
	}

	.mail.new {
		background: color-mix(in oklch, var(--primary) 4%, #fff);
		transform: translateY(-8px);
	}

	.mail.new:global([data-on]) {
		transform: none;
	}

	.mail.new .from,
	.mail.new .subj {
		color: var(--ink);
		font-weight: 600;
	}

	.dot {
		position: absolute;
		left: 9px;
		top: 16px;
		width: 7px;
		height: 7px;
		border-radius: 50%;
		background: var(--primary);
		animation: unread 1.6s ease-in-out infinite;
	}

	@keyframes unread {
		50% {
			opacity: 0.35;
		}
	}

	/* The RFQ's two files. Their thumbnails are where the big views grow from and fold back into. */
	.files {
		display: flex;
		flex-wrap: wrap;
		gap: 4px;
		margin-top: 3px;
	}

	.file {
		display: inline-flex;
		align-items: center;
		gap: 5px;
		min-width: 0;
		padding: 2px 7px 2px 3px;
		border-radius: 5px;
		background: var(--muted);
		color: var(--foreground);
		font: 500 10.5px/1.4 var(--font-mono);
		white-space: nowrap;
		transition: transform 0.3s var(--pop);
	}

	.files:global([data-press]) .file {
		transform: translateY(1px) scale(0.93);
		transition: transform 0.18s var(--ease);
	}

	.file-thumb {
		flex: none;
		width: 18px;
		height: 14px;
		border-radius: 2px;
		background: #fff;
		object-fit: contain;
		transition: opacity 0.2s var(--ease);
	}

	.files:global([data-out]) .file-thumb {
		opacity: 0;
	}

	.picked {
		display: inline-flex;
		align-items: center;
		gap: 6px;
		margin-top: 6px;
		color: var(--primary);
		font-size: 11.5px;
		font-weight: 600;
		transform: translateY(4px);
	}

	.picked:global([data-on]) {
		transform: none;
	}

	/* The review is pinned to the bottom of the inbox, over the older emails, so it always shows in
	   full however much the emails above it wrap. */
	.inbox {
		position: relative;
	}

	.review {
		position: absolute;
		right: 10px;
		bottom: 10px;
		left: 10px;
		z-index: 2;
		display: flex;
		flex-direction: column;
		gap: 4px;
		margin: 0;
		padding: 12px;
		border-radius: 9px;
		background: #fff;
		box-shadow:
			inset 0 0 0 1px color-mix(in oklch, var(--primary) 35%, transparent),
			0 16px 30px -18px color-mix(in oklch, var(--primary) 55%, transparent);
		transform: translateY(14px) scale(0.97);
	}

	.review:global([data-on]) {
		transform: none;
	}

	.review-k {
		display: inline-flex;
		align-items: center;
		gap: 6px;
		color: var(--primary);
		font-size: 11.5px;
		font-weight: 600;
	}

	.review-t {
		color: var(--ink);
		font-size: 13.5px;
		font-weight: 600;
	}

	.review-s {
		color: var(--muted-foreground);
		font-size: 11.5px;
	}

	.review-s b {
		color: var(--ink);
		font-weight: 600;
	}

	/* The buttons wrap in a narrow inbox rather than run off the card. */
	.btns {
		display: flex;
		flex-wrap: wrap;
		gap: 6px;
		margin-top: 6px;
	}

	.btn {
		display: inline-flex;
		align-items: center;
		height: 28px;
		padding: 0 10px;
		border-radius: 6px;
		font-size: 12px;
		font-weight: 600;
		white-space: nowrap;
	}

	.btn.ghost {
		box-shadow: inset 0 0 0 1px var(--line);
		color: var(--foreground);
	}

	/* The click turns "Approve" into "Approved", and the button goes green. Both labels share one
	   cell, so the button keeps its width. */
	.btn.approve {
		display: inline-grid;
		place-items: center;
		background: var(--primary);
		color: #fff;
		transition: background-color 0.3s var(--ease);
	}

	.btn.approve:global(:has(.done[data-on])) {
		background: var(--ok);
	}

	.ap-label {
		grid-area: 1 / 1;
		display: inline-flex;
		align-items: center;
		gap: 4px;
		transform: translateY(4px);
	}

	.ap-label:global([data-on]) {
		transform: none;
	}

	/* "Approved" pops in as the press releases (follow-through). */
	.ap-label.done:global([data-on]) {
		animation: approved 0.45s var(--pop) both;
	}

	@keyframes approved {
		from {
			transform: scale(0.7);
		}
	}

	/* Once approved, "Request changes" grays out, and "by you" follows the green button under a
	   highlighter stroke that sweeps in from the left. */
	.btn.ghost {
		transition:
			opacity 0.3s var(--ease),
			color 0.3s var(--ease);
	}

	.btns:global(:has(.done[data-on])) .btn.ghost {
		opacity: 0.45;
		color: var(--muted-foreground);
	}

	.by-you {
		align-self: center;
		padding: 1px 4px;
		background: linear-gradient(color-mix(in oklch, var(--ok) 26%, transparent), color-mix(in oklch, var(--ok) 26%, transparent)) no-repeat 0 85% / 0% 60%;
		color: var(--ink);
		font-size: 12.5px;
		font-weight: 600;
		transition:
			opacity 0.2s var(--ease),
			background-size 0.5s var(--ease) 0.15s;
	}

	.by-you:global([data-on]) {
		background-size: 100% 60%;
	}

	.review-from {
		margin-top: -2px;
		color: var(--muted-foreground);
		font: 500 10.5px var(--font-mono);
	}

	/* Thread: messages stack from the bottom, so new ones push older ones up. */
	.thread .title {
		min-width: 0;
		overflow: hidden;
	}

	/* The crew strip, in the old /enterprise page's style: an overlapping stack of avatars, each in a 2px
	   white frame. When an agent works, its face folds away and the frame becomes a tinted pill with
	   typing dots and what it's doing. When it's done, the face pops back with a small overshoot. A
	   pinged agent hops. */
	.crew {
		display: inline-flex;
		flex: none;
		align-items: center;
	}

	.crew-av {
		display: inline-flex;
		position: relative;
		z-index: 0;
		align-items: center;
		height: 28px;
		margin-left: -6px;
		border: 2px solid #fff;
		border-radius: 8px;
		background-color: transparent;
		box-shadow: inset 0 0 0 1px transparent;
		transition:
			background-color 0.35s cubic-bezier(0.16, 1, 0.3, 1),
			box-shadow 0.35s cubic-bezier(0.16, 1, 0.3, 1),
			z-index 0s 0.35s;
	}

	.crew-av:first-child {
		margin-left: 0;
	}

	.crew-av[data-agent="pre6"] {
		--c: var(--agent-pre6);
	}

	.crew-av[data-agent="rfq"] {
		--c: var(--agent-rfq);
	}

	.crew-av[data-agent="prod"] {
		--c: var(--agent-prod);
	}

	.crew-av[data-agent="qa"] {
		--c: var(--agent-qa);
	}

	.crew-av:global([data-open]) {
		z-index: 1;
		background-color: color-mix(in oklch, var(--c) 9%, #fff);
		box-shadow: inset 0 0 0 1px color-mix(in oklch, var(--c) 28%, #fff);
		transition:
			background-color 0.35s cubic-bezier(0.16, 1, 0.3, 1),
			box-shadow 0.35s cubic-bezier(0.16, 1, 0.3, 1);
	}

	/* The face folds away while the pill is open, then pops back (anticipation and follow-through). */
	.crew-av > .av {
		max-width: 24px;
		transition:
			max-width 0.45s cubic-bezier(0.16, 1, 0.3, 1),
			opacity 0.3s var(--ease),
			transform 0.5s var(--pop);
	}

	.crew-av:global([data-open]) > .av {
		max-width: 0;
		opacity: 0;
		transform: scale(0.8);
		transition:
			max-width 0.45s cubic-bezier(0.16, 1, 0.3, 1),
			opacity 0.2s var(--ease),
			transform 0.3s var(--ease);
	}

	/* As its pill opens, the avatar squeezes in a little, then stretches out (squash and stretch). */
	.crew-av:global([data-open]) {
		animation: pill-in 0.4s var(--ease);
	}

	@keyframes pill-in {
		30% {
			scale: 0.94 1.04;
		}
	}

	.crew-av:global([data-ping]) {
		transform-origin: center bottom;
		animation: hop 0.6s var(--ease);
	}

	.crew-av:global([data-open][data-ping]) {
		animation:
			pill-in 0.4s var(--ease),
			hop 0.6s var(--ease);
	}

	/* The hop crouches first, stretches on the way up and squashes as it lands (anticipation, squash and stretch). */
	@keyframes hop {
		0%,
		100% {
			transform: none;
		}
		18% {
			transform: translateY(1px) scale(1.06, 0.9);
		}
		42% {
			transform: translateY(-4px) scale(0.95, 1.06);
		}
		72% {
			transform: translateY(0) scale(1.05, 0.93);
		}
	}

	.crew .say-in {
		gap: 7px;
	}

	.crew .say-dots {
		gap: 3px;
		margin-left: 10px;
	}

	.crew .say-dots i {
		width: 4px;
		height: 4px;
	}

	.crew .say-l,
	.crew .say-s {
		padding-right: 10px;
		color: color-mix(in oklch, var(--c) 70%, #000);
		font-size: 12px;
		line-height: 16px;
	}

	/* One label per thing the agent does; it opens from zero width, so a change of task slides rather than jumps. */
	.say {
		display: grid;
		grid-template-columns: 0fr;
		transition:
			grid-template-columns 0.45s var(--ease),
			opacity 0.25s var(--ease);
	}

	.say:global([data-on]) {
		grid-template-columns: 1fr;
		transition:
			grid-template-columns 0.5s var(--ease) 0.05s,
			opacity 0.3s var(--ease) 0.12s;
	}

	.say-in {
		display: inline-flex;
		align-items: center;
		gap: 6px;
		min-width: 0;
		overflow: hidden;
		white-space: nowrap;
	}

	.say-dots {
		display: inline-flex;
		flex: none;
		gap: 2.5px;
		margin-left: 7px;
	}

	.say-dots i {
		width: 3.5px;
		height: 3.5px;
		border-radius: 50%;
		background: var(--c);
		opacity: 0.3;
		animation: dot 1.1s ease-in-out infinite;
	}

	.say-dots i:nth-child(2) {
		animation-delay: 0.15s;
	}

	.say-dots i:nth-child(3) {
		animation-delay: 0.3s;
	}

	/* The label fills the pill's full 20px height, so descenders aren't clipped. */
	.say-l,
	.say-s {
		padding-right: 8px;
		color: color-mix(in oklch, var(--c) 72%, #000);
		font-size: 11px;
		font-weight: 600;
		line-height: 20px;
	}

	.say-s {
		display: none;
	}

	/* The crew strip always uses the short label, so it reads the same at every width. */
	.crew .say-l {
		display: none;
	}

	.crew .say-s {
		display: inline;
	}

	/* Older messages scroll up and fade out under the header, instead of being sliced mid-line. */
	.msgs {
		flex: 1;
		min-height: 0;
		overflow: hidden;
		mask-image: linear-gradient(to bottom, transparent 0, #000 44px);
	}

	/* Before the clock runs: the finished thread, sitting at the bottom of the window. */
	.msgs-in {
		display: flex;
		flex-direction: column;
		justify-content: flex-end;
		gap: 2px;
		min-height: 100%;
		padding: 8px 12px 12px;
	}

	.spacer {
		display: none;
		flex: none;
	}

	/* With the clock running: messages stack downward under a window-tall spacer, and the window
	   scrolls (see the script), so nothing already on screen is moved by layout. */
	.story:global([data-live]) .msgs-in {
		justify-content: flex-start;
	}

	.story:global([data-live]) .spacer {
		display: block;
	}

	.story:global([data-boot]) .row {
		transition: none;
	}

	.sys {
		align-self: center;
		margin: 0 0 6px;
		color: var(--muted-foreground);
		font: 500 10.5px/1.3 var(--font-mono);
	}

	/* A row opens from zero height, then fades in: the thread grows instead of jumping. */
	.row {
		display: grid;
		grid-template-rows: 0fr;
		transition:
			grid-template-rows 0.4s var(--ease),
			opacity 0.3s var(--ease);
	}

	.row:global([data-on]) {
		grid-template-rows: 1fr;
	}

	.row > :global(*) {
		min-height: 0;
		overflow: hidden;
	}

	.typing.row {
		display: flex;
		align-items: center;
		gap: 8px;
		max-height: 0;
		padding: 0;
		transition:
			max-height 0.25s var(--ease),
			padding 0.25s var(--ease),
			opacity 0.2s var(--ease);
	}

	.typing.row:global([data-on]) {
		max-height: 40px;
		padding: 6px 0;
	}

	/* The dots spring up as an agent starts typing, and squash down into the message as it lands
	   (squash and stretch). */
	.dots {
		display: inline-flex;
		gap: 3px;
		padding: 8px 10px;
		border-radius: 10px;
		background: var(--muted);
		transform: scale(1.12, 0.45);
		transform-origin: left bottom;
		transition: transform 0.2s var(--ease);
	}

	.typing.row:global([data-on]) .dots {
		transform: none;
		transition: transform 0.4s var(--pop);
	}

	.dots i {
		width: 5px;
		height: 5px;
		border-radius: 50%;
		background: var(--muted-foreground);
		opacity: 0.35;
		animation: dot 1s ease-in-out infinite;
	}

	.dots i:nth-child(2) {
		animation-delay: 0.15s;
	}

	.dots i:nth-child(3) {
		animation-delay: 0.3s;
	}

	@keyframes dot {
		40% {
			opacity: 1;
			transform: translateY(-2px);
		}
	}

	.msg-in {
		display: flex;
		gap: 8px;
		margin: 0 -6px;
		padding: 6px;
		border-radius: 8px;
	}

	/* Each new message lights up briefly in its agent's color as it lands, so the eye finds it. */
	.row.msg:global([data-on]) .msg-in {
		animation:
			land 1.8s var(--ease),
			settle 0.55s var(--ease) both;
	}

	/* A message rises into place, overshoots a pixel and settles (slow in and out, follow-through). */
	@keyframes settle {
		0% {
			transform: translateY(6px);
		}
		65% {
			transform: translateY(-1px);
		}
		100% {
			transform: none;
		}
	}

	@keyframes land {
		0%,
		35% {
			background: color-mix(in oklch, var(--fc, var(--primary)) 9%, transparent);
		}
		100% {
			background: transparent;
		}
	}

	.bubble {
		display: flex;
		flex-direction: column;
		gap: 3px;
		min-width: 0;
	}

	.name {
		font-size: 11.5px;
		font-weight: 600;
	}

	.bubble p {
		margin: 0;
		color: var(--foreground);
		font-size: 12.5px;
		line-height: 1.45;
	}

	/* The answer's "No." lands with a small pop, a beat after its message (exaggeration, staging). */
	.pop {
		display: inline-block;
		color: var(--ink);
		font-weight: 700;
	}

	.row.msg:global([data-on]) .pop {
		animation: no-pop 0.5s var(--pop) 0.3s both;
	}

	@keyframes no-pop {
		from {
			transform: scale(1.4);
		}
	}

	/* Facts the eye should catch while skimming: ink and semibold inside the regular-weight message. */
	.bubble p b {
		color: var(--ink);
		font-weight: 600;
	}

	/* A reply quotes the line it answers, in the color of the agent who wrote it. */
	.reply {
		display: flex;
		align-items: center;
		gap: 5px;
		align-self: flex-start;
		max-width: 100%;
		margin: 1px 0 2px;
		padding: 2px 8px 2px 6px;
		border-left: 2px solid var(--c);
		border-radius: 0 6px 6px 0;
		background: color-mix(in oklch, var(--c) 6%, #fff);
		color: var(--muted-foreground);
		font-size: 11px;
		line-height: 1.35;
	}

	.reply :global(svg) {
		flex: none;
		color: var(--c);
	}

	.reply b {
		flex: none;
		color: var(--c);
		font-weight: 600;
	}

	.mention {
		padding: 0 3px;
		border-radius: 4px;
		background: color-mix(in oklch, var(--c) 12%, #fff);
		color: var(--c);
		font-weight: 600;
	}

	.attach {
		display: inline-flex;
		align-items: center;
		gap: 6px;
		align-self: flex-start;
		max-width: 100%;
		margin-top: 4px;
		padding: 5px 8px;
		border-radius: 7px;
		background: color-mix(in oklch, var(--c) 6%, #fff);
		box-shadow: inset 0 0 0 1px color-mix(in oklch, var(--c) 30%, transparent);
		color: var(--c);
		font-size: 11.5px;
		transform: scale(0.92);
		transform-origin: left center;
		transition:
			opacity 0.2s var(--ease),
			transform 0.4s var(--pop);
	}

	.attach:global([data-on]) {
		transform: none;
	}

	.attach b {
		flex: none;
		font: 600 11px var(--font-mono);
		white-space: nowrap;
	}

	/* The chip's text wraps inside it instead of being cut off. */
	.attach span {
		min-width: 0;
		color: var(--muted-foreground);
	}

	/* Memory: a searchable timeline of past jobs. When the panel is narrow, the job count and the two
	   jobs the agents never cite step aside, so the rest fit without being cut off. */
	.memory {
		container-type: inline-size;
	}

	@container (max-width: 330px) {
		.count,
		.job[data-i="0"],
		.job[data-i="6"] {
			display: none;
		}
	}

	/* A narrow inbox keeps just the time; a narrow thread drops its "#" and tightens the crew strip, so
	   neither header is ever cut off. */
	.inbox,
	.thread {
		container-type: inline-size;
	}

	@container (max-width: 260px) {
		.clk-x {
			display: none;
		}
	}

	@container (max-width: 380px) {
		.hash {
			display: none;
		}

		.crew {
			zoom: 0.86;
		}
	}

	/* Two agents at work at once in a narrow thread: the second one shows just its typing dots, so the
	   title keeps its room. */
	@container (max-width: 460px) {
		.crew-av:global([data-open]) ~ .crew-av:global([data-open]) .say-l,
		.crew-av:global([data-open]) ~ .crew-av:global([data-open]) .say-s {
			display: none;
		}
	}

	@container (max-width: 300px) {
		.thread .head {
			flex-direction: column;
			align-items: flex-start;
			gap: 4px;
			padding-block: 8px;
		}
	}

	/* Memory: a searchable timeline of past jobs. */
	.search {
		display: flex;
		align-items: center;
		gap: 7px;
		margin: 10px 10px 6px;
		padding: 7px 9px;
		border-radius: 7px;
		background: var(--surface);
		box-shadow: inset 0 0 0 1px var(--line);
		color: var(--muted-foreground);
		font: 500 11.5px/1 var(--font-mono);
	}

	.q-who {
		width: 8px;
		height: 8px;
		flex: none;
		border-radius: 50%;
		background: var(--c, transparent);
		opacity: 0;
		transition: opacity 0.2s ease;
	}

	.q-who:global([data-on]) {
		opacity: 1;
	}

	.q-text {
		overflow: hidden;
		color: var(--ink);
		white-space: nowrap;
	}

	.caret {
		width: 1px;
		height: 13px;
		background: var(--ink);
		animation: caret 1s steps(1) infinite;
	}

	@keyframes caret {
		50% {
			opacity: 0;
		}
	}

	.jobs {
		position: relative;
		display: flex;
		flex-direction: column;
		gap: 6px;
		margin: 0;
		padding: 4px 10px 10px 26px;
		list-style: none;
	}

	/* The timeline's spine. */
	.jobs::before {
		content: "";
		position: absolute;
		left: 15px;
		top: 6px;
		bottom: 14px;
		width: 1px;
		background: var(--line);
	}

	.job,
	.fly {
		position: relative;
		display: flex;
		align-items: center;
		gap: 8px;
		padding: 6px 8px;
		border-radius: 7px;
		background: #fff;
		box-shadow: inset 0 0 0 1px var(--line);
		font-size: 11px;
		transition:
			opacity 0.3s var(--ease),
			box-shadow 0.3s var(--ease),
			background-color 0.3s var(--ease),
			transform 0.35s var(--ease);
	}

	.job::before {
		content: "";
		position: absolute;
		left: -15px;
		top: 50%;
		width: 7px;
		height: 7px;
		margin-top: -3.5px;
		border-radius: 50%;
		background: #fff;
		box-shadow: inset 0 0 0 1.5px var(--line);
	}

	/* While a search runs, the matches lift and the rest settle back (secondary action). */
	.memory:global([data-searching]) .job {
		opacity: 0.38;
		transform: translateY(1px) scale(0.99);
	}

	.memory:global([data-searching]) .job:global([data-hit]) {
		opacity: 1;
		background: color-mix(in oklch, var(--c) 6%, #fff);
		box-shadow:
			inset 0 0 0 1px color-mix(in oklch, var(--c) 50%, transparent),
			0 8px 18px -12px var(--c);
		transform: translate(-3px, -2px);
	}

	.job:global([data-hit])::before {
		background: var(--c);
		box-shadow: none;
	}

	.job:global([data-taken]) {
		transform: translate(-3px, -2px) scale(0.98);
	}

	/* The card about to be cited pulses once, then flies (anticipation). */
	.job:global([data-cite]) {
		animation: cite 0.35s var(--pop);
	}

	@keyframes cite {
		50% {
			scale: 1.04;
			box-shadow:
				inset 0 0 0 1.5px var(--c),
				0 0 0 4px color-mix(in oklch, var(--c) 18%, transparent);
		}
	}

	.yr {
		flex: none;
		color: var(--muted-foreground);
		font: 600 10px/1 var(--font-mono);
	}

	.job-main {
		display: flex;
		flex-direction: column;
		gap: 2px;
		min-width: 0;
	}

	.job-main b {
		color: var(--ink);
		font: 600 11px/1.2 var(--font-mono);
	}

	/* Past jobs wrap rather than cut off, so every line reads in full at any width. */
	.job-main span {
		color: var(--muted-foreground);
		overflow-wrap: anywhere;
	}

	/* The memory in flight, from its card into the message that cites it. */
	.fly {
		position: absolute;
		left: 0;
		top: 0;
		z-index: 3;
		background: color-mix(in oklch, var(--c) 6%, #fff);
		box-shadow:
			inset 0 0 0 1px color-mix(in oklch, var(--c) 55%, transparent),
			0 16px 30px -14px color-mix(in oklch, var(--c) 70%, transparent);
		opacity: 0;
		pointer-events: none;
		will-change: transform, opacity;
		transition: none;
	}

	/*
	 * The drawing moment. A wash covers the stage so the two files are the only thing to look at.
	 * The files sit in their resting layout; JS grows them from the email's thumbnails and back.
	 */
	.viewer {
		position: absolute;
		inset: 0;
		z-index: 5;
		background: color-mix(in oklch, var(--surface) 86%, transparent);
		pointer-events: none;
		transition: opacity 0.4s var(--ease);
	}

	.sheets {
		position: absolute;
		inset: 14px;
		z-index: 6;
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		gap: 12px;
		container-type: size;
		pointer-events: none;
	}

	.v-status {
		display: inline-flex;
		align-items: center;
		height: 22px;
		padding: 1px;
		border-radius: 7px;
		background: color-mix(in oklch, var(--c) 9%, #fff);
		box-shadow:
			inset 0 0 0 1px color-mix(in oklch, var(--c) 30%, transparent),
			0 10px 24px -16px var(--c);
		opacity: 0;
		transform: translateY(6px);
		transition:
			opacity 0.3s var(--ease),
			transform 0.4s var(--ease);
	}

	.story:global([data-live]) .viewer:global([data-on]) ~ .sheets .v-status {
		opacity: 1;
		transform: none;
	}

	.docs {
		display: flex;
		align-items: center;
		justify-content: center;
		gap: 4cqw;
	}

	.doc {
		position: relative;
		display: flex;
		flex-direction: column;
		margin: 0;
		overflow: hidden;
		border-radius: 10px;
		background: #fff;
		box-shadow:
			0 0 0 1px var(--line),
			0 30px 60px -30px oklch(0.2 0.05 264 / 0.45);
		opacity: 0;
		will-change: transform, opacity;
	}

	.doc-head {
		display: flex;
		align-items: center;
		gap: 7px;
		height: 30px;
		padding: 0 10px;
		border-bottom: 1px solid var(--line);
		color: var(--muted-foreground);
		font: 500 11px/1 var(--font-mono);
		white-space: nowrap;
	}

	.doc-name {
		overflow: hidden;
		color: var(--ink);
		text-overflow: ellipsis;
	}

	.doc-tag {
		flex: none;
		margin-left: auto;
		padding: 3px 6px;
		border-radius: 4px;
		background: var(--muted);
		font-size: 10px;
	}

	.frame {
		position: relative;
		overflow: hidden;
		width: min(56cqw, (100cqh - 84px) * 1.4231);
	}

	.doc.model .frame {
		width: min(34cqw, (100cqh - 84px) * 1.0235);
		background: radial-gradient(60% 50% at 50% 55%, oklch(0.97 0.01 255), #fff);
		perspective: 900px;
	}

	.frame img {
		display: block;
		width: 100%;
		height: auto;
	}

	/* The model turns on a slow 3D tilt; its reticle rides on the same plane. */
	.tilt {
		position: relative;
		width: 84%;
		margin: 5% auto 9%;
		transform-style: preserve-3d;
		will-change: transform;
	}

	.doc.model .frame::after {
		content: "";
		position: absolute;
		left: 18%;
		right: 18%;
		bottom: 2%;
		height: 6%;
		border-radius: 50%;
		background: radial-gradient(closest-side, oklch(0.2 0.05 264 / 0.18), transparent);
		z-index: -1;
	}

	/* The bore's tolerance note: the only dimension the review highlights. */
	.mark {
		position: absolute;
		border-radius: 2px;
		opacity: 0;
		transform: scale(1.4);
		transition:
			opacity 0.25s var(--ease),
			transform 0.4s var(--pop);
	}

	.mark:global([data-on]) {
		opacity: 1;
		transform: none;
	}

	/* The bore's tolerance note stays above the wash, so it reads as part of the bore. */
	.mark.key {
		z-index: 2;
		background: color-mix(in oklch, var(--qa) 14%, transparent);
		box-shadow:
			0 0 0 2px var(--qa),
			0 0 0 6px color-mix(in oklch, var(--qa) 16%, transparent);
	}

	.mark.key:global([data-on]) {
		opacity: 1;
	}

	/* The scan line: a full-width layer slid across with a transform (no layout per frame, so no
	   layout shift), with the line at its left edge and a soft wake trailing behind it. */
	.scan {
		position: absolute;
		inset: 0;
		pointer-events: none;
		translate: calc(var(--p, 0) * 100%) 0;
		opacity: 0;
		transition: opacity 0.2s var(--ease);
	}

	.scan::after {
		content: "";
		position: absolute;
		top: 0;
		bottom: 0;
		left: -1px;
		width: 2px;
		background: var(--qa);
		box-shadow: 0 0 12px 1px color-mix(in oklch, var(--qa) 60%, transparent);
	}

	.scan::before {
		content: "";
		position: absolute;
		top: 0;
		bottom: 0;
		left: -71px;
		width: 70px;
		background: linear-gradient(90deg, transparent, color-mix(in oklch, var(--qa) 12%, transparent));
	}

	.scan:global([data-on]) {
		opacity: 1;
	}

	/* The reticle starts wide and turned, then snaps onto the bore with a little overshoot. */
	.reticle {
		position: absolute;
		left: var(--x);
		top: var(--y);
		width: var(--w);
		aspect-ratio: var(--ar);
		border-radius: 50%;
		z-index: 1;
		/* The ring, a soft halo, a light tint inside, and a white wash over everything outside it, so
		   the bore is the one thing left in focus (staging). */
		box-shadow:
			0 0 0 2.5px var(--qa),
			0 0 0 7px color-mix(in oklch, var(--qa) 18%, transparent),
			0 0 0 9999px color-mix(in oklch, #fff 62%, transparent),
			inset 0 0 0 999px color-mix(in oklch, var(--qa) 6%, transparent);
		translate: -50% -50%;
		transform: scale(2.3) rotate(-70deg);
		transition:
			opacity 0.25s var(--ease),
			transform 0.55s var(--pop);
	}

	.reticle:global([data-on]) {
		transform: none;
	}

	/* Crosshair ticks outside the ring. */
	.reticle::before,
	.reticle::after {
		content: "";
		position: absolute;
		inset: -9px 50%;
		width: 2px;
		margin-left: -1px;
		background: linear-gradient(var(--qa) 7px, transparent 7px calc(100% - 7px), var(--qa) calc(100% - 7px));
	}

	.reticle::after {
		inset: 50% -9px;
		width: auto;
		height: 2px;
		margin: -1px 0 0;
		background: linear-gradient(90deg, var(--qa) 7px, transparent 7px calc(100% - 7px), var(--qa) calc(100% - 7px));
	}

	.link {
		position: absolute;
		inset: 0;
		z-index: 7;
		width: 100%;
		height: 100%;
		overflow: visible;
		pointer-events: none;
	}

	.link circle {
		fill: var(--qa);
		stroke: #fff;
		stroke-width: 2;
		opacity: 0;
	}

	.link path {
		fill: none;
		stroke: var(--qa);
		stroke-width: 1.5;
		stroke-linecap: round;
		stroke-dasharray: 1;
		stroke-dashoffset: 1;
		opacity: 0;
		filter: drop-shadow(0 0 3px #fff);
	}

	/* The callout: the bore, as the Quality Agent flags it. It pops at the link's apex, then flies into the thread. */
	.attach.callout {
		position: absolute;
		left: 0;
		top: 0;
		z-index: 8;
		margin: 0;
		background: #fff;
		box-shadow:
			inset 0 0 0 1.5px var(--c),
			0 16px 30px -12px color-mix(in oklch, var(--c) 60%, transparent);
		opacity: 0;
		transform-origin: 0 0;
		pointer-events: none;
		will-change: transform, opacity;
		transition: none;
	}

	.human {
		position: absolute;
		left: 0;
		top: 0;
		z-index: 9;
		width: 16px;
		height: 23px;
		overflow: visible;
		filter: drop-shadow(0 3px 5px oklch(0 0 0 / 0.3));
		opacity: 0;
		transform-origin: 2px 2px;
		pointer-events: none;
	}

	.human path {
		fill: var(--ink);
		stroke: #fff;
		stroke-width: 2.4;
		stroke-linejoin: round;
		paint-order: stroke;
	}

	/* Without the clock running (no JS yet, or reduced motion before mount): the finished story. */
	.story:not(:global([data-live])) [data-at]:not([data-until]) {
		opacity: 1;
	}

	.story:not(:global([data-live])) .row.msg {
		grid-template-rows: 1fr;
	}

	.story:not(:global([data-live])) .night {
		opacity: 0;
	}

	/* Narrower: the memory hides (attachments still arrive); on phones the inbox sits above the thread. */
	/* Mid widths: the thread gets the extra room, so its header never runs out of space. */
	@media (min-width: 900px) and (max-width: 1100px) {
		.story {
			grid-template-columns: minmax(0, 0.85fr) minmax(0, 1.45fr) minmax(0, 0.95fr);
		}
	}

	@media (max-width: 899px) {
		.story {
			grid-template-columns: minmax(0, 0.8fr) minmax(0, 1.2fr);
			aspect-ratio: auto;
			height: 520px;
		}

		.memory {
			display: none;
		}

		.frame {
			width: min(57cqw, (100cqh - 84px) * 1.4231);
		}

		.doc.model .frame {
			width: min(37cqw, (100cqh - 84px) * 1.0235);
		}
	}

	@media (max-width: 599px) {
		.story {
			grid-template-columns: minmax(0, 1fr);
			grid-template-rows: auto minmax(0, 1fr);
			height: 620px;
			padding: 10px;
			gap: 10px;
		}

		.mail:not(.new) {
			display: none;
		}

		/* The review lands over the email, so the inbox stays short and the thread gets the room. */
		.inbox {
			position: relative;
			min-height: 226px;
		}

		.review {
			top: 52px;
			bottom: auto;
		}

		/* The narrowest phones wrap the review card onto more lines, so the inbox grows to hold it. */
		@media (max-width: 359px) {
			.inbox {
				min-height: 258px;
			}
		}

		/* The two files stack: drawing above, model below. */
		.sheets {
			inset: 10px;
			gap: 10px;
		}

		.docs {
			flex-direction: column;
			gap: 10px;
		}

		.frame {
			width: min(100cqw, (100cqh - 150px) * 0.56 * 1.4231);
		}

		.doc.model .frame {
			width: calc((100cqh - 150px) * 0.5 * 1.0235);
		}

		.doc.model .doc-tag {
			display: none;
		}
	}

	@media (prefers-reduced-motion: reduce) {
		.dot,
		.dots i,
		.say-dots i,
		.caret,
		.crew-av:global([data-ping]),
		.crew-av:global([data-open]),
		.job:global([data-cite]),
		.ap-label,
		.pop,
		.mail.new::after,
		.msg-in {
			animation: none !important;
		}
	}
</style>

